/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from 'react';
import styles from '@buddieshr/styles/components/turnover/employeeTurnoverCalculator.module.scss';
import { Button, Divider, Input, Modal } from 'antd';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import TextColored from '../text/textColored';
import { useTranslation } from 'react-i18next';
import Link from 'next/link';
import { ANALYTICS_EVENTS } from '@buddieshr/utils/constants';
import { gtagEvent } from '@buddieshr/utils/gtag';
import { getTotalCostPerEmployee } from '@buddieshr/utils/utils';

const EmployeeTurnoverCalculator = () => {
  const { t } = useTranslation();
  const [step, setStep] = useState(0);
  const [isNextDisabled, setIsNextDisabled] = useState(true);
  const [isCalculationDetailsOpen, setIsCalculationDetailsOpen] =
    useState(false);
  const [numberOfEmployees, setNumberOfEmployees] = useState(undefined);
  const [turnoverRate, setTurnoverRate] = useState(undefined);
  const [averageSalary, setAverageSalary] = useState(undefined);
  const [totalCost, setTotalCost] = useState(null);
  const [currentTemplate, setCurrentTemplate] = useState(null);
  const NUMBER_STEPS = 3;

  const onChangeNumberEmployee = (e) => {
    setNumberOfEmployees(parseInt(e.target.value));
  };
  const onChangeTurnoverRate = (e) => {
    setTurnoverRate(parseInt(e.target.value));
  };
  const onChangeAverageSalary = (e) => {
    setAverageSalary(parseInt(e.target.value));
  };

  const updateTotalCost = () => {
    const totalPerEmployee = getTotalCostPerEmployee(averageSalary);
    setTotalCost(totalPerEmployee * (numberOfEmployees * (turnoverRate / 100)));
  };

  const getStepTemplate = (stepParam) => {
    switch (stepParam) {
      case 0:
        return (
          <div>
            <div className={styles.question}>
              How many employees does your company have?
            </div>
            <div className={styles.input}>
              <Input
                autoFocus
                key="nbe"
                placeholder="0 - 100000"
                onChange={onChangeNumberEmployee}
                value={numberOfEmployees}
              />
            </div>
          </div>
        );
      case 1:
        return (
          <div>
            <div className={styles.question}>What’s your turnover rate?</div>
            <div className={styles.input}>
              <Input
                autoFocus
                key="turnover"
                placeholder="0 - 100"
                suffix="%"
                onChange={onChangeTurnoverRate}
                value={turnoverRate}
              />
            </div>
            <div className={styles.hint}>US average: 18%</div>
          </div>
        );
      case 2:
        return (
          <div>
            <div className={styles.question}>
              What’s the average salary at your company?
            </div>
            <div className={styles.input}>
              <Input
                autoFocus
                key="average"
                placeholder="67000"
                onChange={onChangeAverageSalary}
                value={averageSalary}
              />
            </div>
            <div className={styles.hint}>US average: $67,000</div>
          </div>
        );
      case 3:
        return (
          <div>
            <div className={styles.finalstep}>
              <div className={styles.turnoverCost}>
                Yearly turnover cost is{' '}
                <TextColored>
                  ${parseFloat(totalCost?.toFixed(0)).toLocaleString('en-US')}
                </TextColored>{' '}
                at your company based on your turnover rate of {turnoverRate}%.
              </div>
              <div>
                <a
                  onClick={() => {
                    setIsCalculationDetailsOpen(true);
                  }}
                  className={styles.calcDetailsLink}
                >
                  View calculation details
                </a>
              </div>
              <Divider />
              <div className={styles.reduce}>
                <TextColored>
                  BuddiesHR can reduce by 33% your yearly turnover
                </TextColored>
              </div>
              <div className={styles.install}>Install BuddiesHR today and</div>
              <div className={styles.save}>SAVE</div>
              <div className={styles.saving}>
                $
                {parseFloat(
                  (totalCost * 0.33 - numberOfEmployees * 1 * 12)?.toFixed(0)
                ).toLocaleString('en-US')}
              </div>
              <div className={styles.perYear}>PER YEAR</div>
              <div className={styles.costDeducted}>
                Cost of BuddiesHR for {numberOfEmployees} employees deducted.{' '}
                <Link href="/pricing" title="Pricing recognition">
                  (View pricing)
                </Link>
              </div>
            </div>
          </div>
        );
    }
  };
  const updateCurrentTemplate = (stepParam) => {
    setCurrentTemplate(getStepTemplate(stepParam));
  };

  const next = () => {
    if (step < NUMBER_STEPS) {
      const newStep = step + 1;
      setStep(newStep);
      updateCurrentTemplate(newStep);
    }
  };

  const prev = () => {
    if (step > 0) {
      const newStep = step - 1;
      setStep(newStep);
      updateCurrentTemplate(newStep);
    }
  };

  useEffect(() => {
    setIsNextDisabled(
      step === 0
        ? !(!!numberOfEmployees && numberOfEmployees > 0)
        : step === 1
        ? !(!!turnoverRate && turnoverRate > 0)
        : step === 3
        ? !(!!averageSalary && averageSalary > 0)
        : false
    );
    updateTotalCost();
  }, [numberOfEmployees, step, turnoverRate, averageSalary]);

  useEffect(() => {
    updateCurrentTemplate(0);
  }, []);

  return (
    <div className={styles.wrapper}>
      <div className={styles.inner}>
        <h3 className={styles.title}>
          Employee turnover cost calculator (Free)
        </h3>
        <div>{currentTemplate}</div>
        <div className={styles.actions}>
          <div className={styles.prev}>
            {step > 0 && (
              <Button
                type={step < NUMBER_STEPS ? 'primary' : 'secondary'}
                onClick={() => {
                  prev();
                }}
              >
                <LeftOutlined /> {step < NUMBER_STEPS ? 'Prev' : ''}
              </Button>
            )}
          </div>
          {step < NUMBER_STEPS && (
            <Button
              type="primary"
              onClick={() => {
                next();
              }}
              disabled={isNextDisabled}
            >
              Next <RightOutlined />
            </Button>
          )}
          {step >= NUMBER_STEPS && (
            <Link
              href="/get-started"
              onClick={() => {
                gtagEvent({
                  action: ANALYTICS_EVENTS.GET_STARTED_TURNOVER_CALCULATOR,
                  category: 'get-started',
                  label: 'buddieshr',
                });
              }}
            >
              <Button type="primary">Get Started now</Button>
            </Link>
          )}
        </div>
        {isNextDisabled && (
          <div className={styles.hintBottom}>
            Enter a valid number in the input
          </div>
        )}
      </div>
      {isCalculationDetailsOpen && (
        <Modal
          open={isCalculationDetailsOpen}
          title={'Turnover cost calculation details'}
          onCancel={() => {
            setIsCalculationDetailsOpen(false);
          }}
          width={650}
          footer={[
            <Button
              type="primary"
              key="closebtn"
              onClick={() => {
                setIsCalculationDetailsOpen(false);
              }}
            >
              Close
            </Button>,
          ]}
        >
          <div className={styles.calculationDetailsWrapper}>
            <div className={styles.calculationStep}>
              <h4 className={styles.calculationTitle}>Loss of knowledge</h4>
              <div>
                Average year of tenure = 3 years
                <br />
                <b>Loss of knowledge</b> = 5% * Salary ($
                {averageSalary.toLocaleString('en-US')}) * Average Year of
                tenure (3);
              </div>
            </div>
            <div className={styles.calculationStep}>
              <h4 className={styles.calculationTitle}>
                Loss productivity from other employees filling in for the vacant
                position
              </h4>
              <div>
                Productivity cost percentage = 30%
                <br />
                <b> Productivity cost</b> = Productivity cost percentage (30%) *
                (Salary ( ${averageSalary.toLocaleString('en-US')}) * 20%);
              </div>
            </div>
            <div className={styles.calculationStep}>
              <h4 className={styles.calculationTitle}>
                Cost to hire replacement
              </h4>
              <div>
                Hiring Manager Salary = $68,000; (Work hours per year = 2,000)
                <br />
                Work Hours to Fill Position = 30 hours
                <br />
                Advertising cost = $1,000;
                <br />
                <b>Cost to hire replacement</b> = Advertising cost ($1,000) +
                Cost per hour (Hiring Manager Salary ($68,000) / Work hours per
                year (2,000)) * Work Hours to Fill Position (30h)
                <br />
              </div>
            </div>
            <div className={styles.calculationStep}>
              <h4 className={styles.calculationTitle}>
                New hire training cost
              </h4>
              <div>
                Mentor or manager salary: $100,000
                <br />
                Total training hours : 45 hours
                <br />
                <b>New hire training cost</b> = (Mentor salary ($100,000) / Work
                hours per year (2,000)) * Total training hours (45)
              </div>
            </div>
            <div className={styles.calculationStep}>
              <h4 className={styles.calculationTitle}>
                Return to previous productivity
              </h4>
              <div>
                New hire reduction in productivity: 25%
                <br />
                Weeks to 100% productivity: 25 weeks
                <br />
                Loss per week = Salary per week * New hire reduction in
                productivity (25%)
                <br />
                <b>Productivity ramp up cost</b> = Loss per week * Weeks to 100%
                productivity
              </div>
            </div>
            <div className={styles.calculationStep}>
              <h4 className={styles.calculationTitle}>Total calculation</h4>
              <div>
                Total per employee = Loss of knowledge + Loss productivity cost
                + Cost to hire replacement + New hire training cost + return to
                previous productivity cost <br />
                Number of churning employees per year = Number of employees (
                {numberOfEmployees}) * Turnover rate ({turnoverRate}%)
                <br /> <b>Total cost per year</b> = Total per employee + Number
                of churning employees per year (
                {numberOfEmployees * (turnoverRate / 100)})
              </div>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default EmployeeTurnoverCalculator;
